import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Form,
  Modal,
  Alert,
  InputGroup,
  Dropdown,
} from "react-bootstrap";
import { subscriptionAPI } from "../../api/subscription";
import { membershipAPI } from "../../api/membershipPlans";
import { gymMembersAPI } from "../../api/gymMembers";
import Swal from "sweetalert2";
import {
  FaSearch,
  FaFilter,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaPrint,
  FaDownload,
  FaCalendarAlt,
  FaUser,
} from "react-icons/fa";
import { format } from "date-fns";

const Payments = () => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "cash",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Since we don't have a dedicated pending payments API,
      // we'll get all plans and filter those with pending payments
      const [plansResponse, membersResponse] = await Promise.all([
        membershipAPI.getPlans(),
        gymMembersAPI.getMembers(),
      ]);

      const allPlans = plansResponse.data.data || plansResponse.data || [];
      const allMembers =
        membersResponse.data.data || membersResponse.data || [];

      // Filter plans with pending payments
      const pendingPlans = allPlans.filter(
        (plan) => parseFloat(plan.pending_amount || 0) > 0
      );

      setPendingPayments(pendingPlans);
      setPlans(allPlans);
      setMembers(allMembers);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load pending payments data",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find((m) => m.id === memberId);
    return member
      ? `${member.first_name} ${member.last_name} (${member.member_id})`
      : "Unknown Member";
  };

  const getPlanDetails = (planId) => {
    return plans.find((p) => p.id === planId) || {};
  };

  const filteredPayments = pendingPayments.filter((payment) => {
    const memberName = getMemberName(payment.member).toLowerCase();
    const matchesSearch =
      memberName.includes(searchTerm.toLowerCase()) ||
      payment.id.toString().includes(searchTerm);

    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "pending" && parseFloat(payment.pending_amount) > 0) ||
      (filterStatus === "partial" &&
        parseFloat(payment.paid_amount) > 0 &&
        parseFloat(payment.pending_amount) > 0);

    return matchesSearch && matchesFilter;
  });

  const handleMarkAsPaid = async (paymentId, amount) => {
    const result = await Swal.fire({
      title: "Mark as Paid",
      html: `
        <div class="text-start">
          <p>Are you sure you want to mark this payment as paid?</p>
          <p><strong>Pending Amount:</strong> ₹${amount}</p>
          <p>This will update the payment status to "Paid".</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, mark as paid",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // Here you would call your API to update payment status
        // For now, we'll simulate the update
        Swal.fire({
          icon: "success",
          title: "Payment Updated!",
          text: "Payment has been marked as paid.",
          timer: 1500,
          showConfirmButton: false,
        });

        // Refresh data
        fetchData();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update payment status",
        });
      }
    }
  };

  const handleAddPayment = (payment) => {
    setSelectedPayment(payment);
    setPaymentForm({
      amount: payment.pending_amount || "0",
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: "cash",
      notes: "",
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPayment) return;

    const paymentAmount = parseFloat(paymentForm.amount);
    const pendingAmount = parseFloat(selectedPayment.pending_amount);

    if (paymentAmount > pendingAmount) {
      Swal.fire({
        icon: "error",
        title: "Invalid Amount",
        text: `Payment amount cannot exceed pending amount (₹${pendingAmount})`,
      });
      return;
    }

    try {
      // Here you would call your API to record the payment
      // For now, we'll simulate the payment
      Swal.fire({
        icon: "success",
        title: "Payment Recorded!",
        html: `
          <div class="text-start">
            <p>Payment has been recorded successfully.</p>
            <p><strong>Amount Paid:</strong> ₹${paymentAmount}</p>
            <p><strong>Payment Method:</strong> ${paymentForm.payment_method}</p>
          </div>
        `,
        timer: 1500,
        showConfirmButton: false,
      });

      setShowPaymentModal(false);
      fetchData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to record payment",
      });
    }
  };

  const calculateStats = () => {
    const totalPending = filteredPayments.reduce(
      (sum, payment) => sum + parseFloat(payment.pending_amount || 0),
      0
    );

    const totalPayments = filteredPayments.length;
    const totalPartial = filteredPayments.filter(
      (p) => parseFloat(p.paid_amount || 0) > 0
    ).length;

    return {
      totalPending,
      totalPayments,
      totalPartial,
    };
  };

  const stats = calculateStats();

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-payments-page">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-0">Pending Payments</h2>
            <p className="text-muted mb-0">
              Manage and track pending membership payments
            </p>
          </div>
          <div>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={() => window.print()}
            >
              <FaPrint className="me-1" /> Print
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                // Export functionality
                Swal.fire({
                  icon: "info",
                  title: "Export",
                  text: "Export functionality will be available soon.",
                });
              }}
            >
              <FaDownload className="me-1" /> Export
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center bg-primary text-white">
            <Card.Body>
              <h6 className="card-title">Total Pending Amount</h6>
              <h2>{formatCurrency(stats.totalPending)}</h2>
              <small className="opacity-75">
                Across {stats.totalPayments} payments
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center bg-warning text-dark">
            <Card.Body>
              <h6 className="card-title">Pending Payments</h6>
              <h2>{stats.totalPayments}</h2>
              <small className="opacity-75">Require attention</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center bg-info text-white">
            <Card.Body>
              <h6 className="card-title">Partial Payments</h6>
              <h2>{stats.totalPartial}</h2>
              <small className="opacity-75">Partially paid</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center bg-danger text-white">
          <h5 className="mb-0">
            <FaMoneyBillWave className="me-2" /> Pending Payments List
          </h5>
          <div className="d-flex gap-2">
            <InputGroup size="sm" style={{ width: "250px" }}>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by member name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Dropdown>
              <Dropdown.Toggle variant="light" size="sm">
                <FaFilter className="me-1" /> Filter
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setFilterStatus("all")}>
                  All Payments
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilterStatus("pending")}>
                  Pending Only
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setFilterStatus("partial")}>
                  Partial Payments
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table hover striped>
              <thead>
                <tr>
                  <th>Plan ID</th>
                  <th>Member</th>
                  <th>Total Amount</th>
                  <th>Paid</th>
                  <th>Pending</th>
                  <th>Fee Status</th>
                  <th>Expiry Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <div className="text-muted">
                        <FaMoneyBillWave size={48} className="mb-3" />
                        <h5>No pending payments found</h5>
                        <p>All payments are up to date</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((payment) => {
                    const memberName = getMemberName(payment.member);
                    const planDetails = getPlanDetails(payment.id);

                    return (
                      <tr key={payment.id}>
                        <td>
                          <Badge bg="secondary">#{payment.id}</Badge>
                        </td>
                        <td>
                          <div className="fw-medium">{memberName}</div>
                          <small className="text-muted">
                            Start: {formatDate(payment.plan_start_date)}
                          </small>
                        </td>
                        <td>
                          <div className="fw-bold">
                            {formatCurrency(
                              payment.total_amount || payment.amount
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="text-success">
                            {formatCurrency(payment.paid_amount)}
                          </div>
                        </td>
                        <td>
                          <div className="text-danger fw-bold">
                            {formatCurrency(payment.pending_amount)}
                          </div>
                        </td>
                        <td>
                          <Badge
                            bg={
                              payment.fee_status?.toLowerCase() === "pending"
                                ? "warning"
                                : payment.fee_status?.toLowerCase() === "paid"
                                ? "success"
                                : "secondary"
                            }
                          >
                            {payment.fee_status || "Pending"}
                          </Badge>
                        </td>
                        <td>
                          <div>
                            {formatDate(payment.expiry_date)}
                            <br />
                            <small className="text-muted">
                              <FaCalendarAlt className="me-1" />
                              {(() => {
                                const expiry = new Date(payment.expiry_date);
                                const today = new Date();
                                const diffDays = Math.ceil(
                                  (expiry - today) / (1000 * 60 * 60 * 24)
                                );
                                return `${diffDays} days left`;
                              })()}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleAddPayment(payment)}
                              title="Add Payment"
                            >
                              <FaMoneyBillWave />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() =>
                                handleMarkAsPaid(
                                  payment.id,
                                  payment.pending_amount
                                )
                              }
                              title="Mark as Paid"
                            >
                              <FaCheckCircle />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                // View details - you can implement this
                                Swal.fire({
                                  title: "Payment Details",
                                  html: `
                                    <div class="text-start">
                                      <p><strong>Plan ID:</strong> ${
                                        payment.id
                                      }</p>
                                      <p><strong>Member:</strong> ${memberName}</p>
                                      <p><strong>Total Amount:</strong> ${formatCurrency(
                                        payment.total_amount || payment.amount
                                      )}</p>
                                      <p><strong>Paid Amount:</strong> ${formatCurrency(
                                        payment.paid_amount
                                      )}</p>
                                      <p><strong>Pending Amount:</strong> ${formatCurrency(
                                        payment.pending_amount
                                      )}</p>
                                      <p><strong>Fee Status:</strong> ${
                                        payment.fee_status
                                      }</p>
                                      <p><strong>Start Date:</strong> ${formatDate(
                                        payment.plan_start_date
                                      )}</p>
                                      <p><strong>Expiry Date:</strong> ${formatDate(
                                        payment.expiry_date
                                      )}</p>
                                    </div>
                                  `,
                                  icon: "info",
                                });
                              }}
                              title="View Details"
                            >
                              <FaEye />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
        <Card.Footer className="text-muted d-flex justify-content-between">
          <div>
            Showing {filteredPayments.length} of {pendingPayments.length}{" "}
            pending payments
          </div>
          <div>
            Total Pending Amount:{" "}
            <span className="fw-bold text-danger">
              {formatCurrency(stats.totalPending)}
            </span>
          </div>
        </Card.Footer>
      </Card>

      {/* Payment Modal */}
      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Payment</Modal.Title>
        </Modal.Header>
        {selectedPayment && (
          <Form onSubmit={handlePaymentSubmit}>
            <Modal.Body>
              <div className="alert alert-info mb-3">
                <h6 className="alert-heading">
                  <FaUser className="me-2" />
                  {getMemberName(selectedPayment.member)}
                </h6>
                <p className="mb-1">
                  <strong>Plan ID:</strong> #{selectedPayment.id}
                </p>
                <p className="mb-1">
                  <strong>Pending Amount:</strong>{" "}
                  {formatCurrency(selectedPayment.pending_amount)}
                </p>
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount to Pay (₹) *</Form.Label>
                    <Form.Control
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          amount: e.target.value,
                        })
                      }
                      placeholder="Enter amount"
                      min="0"
                      max={selectedPayment.pending_amount}
                      step="0.01"
                      required
                    />
                    <Form.Text className="text-muted">
                      Maximum: {formatCurrency(selectedPayment.pending_amount)}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Date *</Form.Label>
                    <Form.Control
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          payment_date: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Method *</Form.Label>
                    <Form.Select
                      value={paymentForm.payment_method}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          payment_method: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Transaction Reference</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Optional transaction ID/Reference"
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          transaction_ref: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Any additional notes about this payment..."
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      notes: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowPaymentModal(false)}
              >
                <FaTimesCircle className="me-1" /> Cancel
              </Button>
              <Button variant="primary" type="submit">
                <FaCheckCircle className="me-1" /> Record Payment
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default Payments;
